/* eslint-disable prefer-rest-params, prefer-rest-params, no-sequences, no-unused-expressions */
import { gaId, fbId } from '../../config';

import { getCatalogueThemeId, getActionButtonThemeId } from './Theme';
import { getShowcaseId } from './Showcase';
import {
  getReferrer,
  getPaymentStatus as getSinglePaymentStatus,
  getCompanyFacebookPixelIds,
  getUniversalGoogleAnalyticsId,
  getCompanyGoogleAnalyticsIds
} from './Amalgam';
import { getShowcaseId as getGroupShowcaseId } from './GroupCatalogueAmalgam';
import { sendAnalytics } from './Reseller';

let companyFacebookPixelInitialized = false;
let companyGoogleAnalyticsInitialized = false;

// eslint-disable-next-line func-style
const gtag = function () {
  // Can't use arrow func + destructuring as Google expects
  // arguments objects in dataLayer (not an array of arguments).
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
};

const universalGoogleAnalyticsId = getUniversalGoogleAnalyticsId();

const pushAnalytics = (gaParams, fbParams) => {
  if (gaParams) {
    if (window.ga) {
      window.ga.apply(window.ga, ['send', 'event', ...gaParams]);
      if (universalGoogleAnalyticsId) {
        window.ga.apply(window.ga, ['clientTracker.send', 'event', ...gaParams]);
      }
    }
  }
  if (fbParams) {
    if (window.fbq) {
      window.fbq.call(window.fbq, 'track', ...fbParams);
    }
  }
};

export const initialize = () => {
  if (window.ga && gaId) {
    window.ga('create', gaId, 'auto');
    if (universalGoogleAnalyticsId) {
      window.ga('create', universalGoogleAnalyticsId, 'auto', 'clientTracker');
    }
  }
  if (window.fbq && fbId) {
    window.fbq('init', fbId);
    window.fbq('track', 'PageView');
  }
};

export const initializeSingle = () => {
  if (window.ga) {
    window.ga('set', 'dimension3', getCatalogueThemeId());
    window.ga('set', 'dimension4', getActionButtonThemeId());
    window.ga('set', 'dimension5', getSinglePaymentStatus());
    window.ga('send', 'event', 'pageOpen', getReferrer() || '');
    if (universalGoogleAnalyticsId) {
      window.ga('clientTracker.send', 'event', 'pageOpen', getReferrer() || '');
    }
  }
};

export const initializeGroup = ({ paymentStatus = '' } = {}) => {
  if (window.ga) {
    window.ga('set', 'dimension5', paymentStatus);
    window.ga('send', 'event', 'pageOpen');
    if (universalGoogleAnalyticsId) {
      window.ga('clientTracker.send', 'event', 'pageOpen');
    }
  }
};

export const onRouteChange = ({ callNativeHandler }) => {
  const { pathname } = location;
  if (window.ga) {
    window.ga('set', 'page', pathname);
    window.ga('send', 'pageview');
    if (universalGoogleAnalyticsId) {
      window.ga('clientTracker.set', 'page', pathname);
      window.ga('clientTracker.send', 'pageview');
    }
  }

  if (callNativeHandler) {
    sendAnalytics({
      name: 'pageview',
      props: {
        pathname
      }
    });
  }
};

export const qsTileDownloadClick = (eventName) => {
  const gaParams = ['QSTileDownload', eventName, getShowcaseId()];
  const fbParams = [
    'QSTileDownload',
    eventName,
    {
      showcase_id: getShowcaseId()
    }
  ];
  pushAnalytics(gaParams, fbParams);
  gtag('event', eventName, {
    event_label: 'QSTileDownload',
    event_category: 'Click'
  });
};

export const onGridStarClick = (data, { callNativeHandler } = {}) => {
  const { productId } = data;
  const gaParams = ['GridStarInquiry', 'click', productId];
  const fbParams = [
    'GridStarInquiry',
    {
      product_id: productId
    }
  ];
  pushAnalytics(gaParams, fbParams);
  gtag('event', 'add_to_cart', data);

  if (callNativeHandler) {
    sendAnalytics({
      name: 'add_to_cart',
      props: {
        from: 'catalogue_tile',
        productId
      }
    });
  }
};

export const onResellCardClick = () => {
  const gaParams = ['resell_card', 'click', getShowcaseId()];
  const fbParams = [
    'ResellCardClick',
    {
      showcase_id: getShowcaseId()
    }
  ];
  pushAnalytics(gaParams, fbParams);
};

export const onResellBarClick = () => {
  const gaParams = ['resell_bar', 'click', getShowcaseId()];
  const fbParams = [
    'ResellBarClick',
    {
      showcase_id: getShowcaseId()
    }
  ];
  pushAnalytics(gaParams, fbParams);
};

export const onGalleryScrollToBottomClick = (from) => {
  const gaParams = ['gallery_scroll_to_bottom', 'click', from];
  const fbParams = ['GalleryScrollToBottom'];
  pushAnalytics(gaParams, fbParams);
  gtag('event', 'gallery_scroll_to_bottom', {
    event_label: 'Gallery Scroll to Bottom',
    event_category: 'Click'
  });
};

export const onStarClick = (data, { callNativeHandler } = {}) => {
  const { productId } = data;
  const gaParams = ['star_inquiry', 'click', productId];
  const fbParams = [
    'StarInquiry',
    {
      product_id: productId
    }
  ];
  pushAnalytics(gaParams, fbParams);
  gtag('event', 'add_to_cart', data);

  if (callNativeHandler) {
    sendAnalytics({
      name: 'add_to_cart',
      props: {
        from: 'gallery_product_view',
        productId
      }
    });
  }
};

export const onInquiryBSubmit = (data, { callNativeHandler } = {}) => {
  const { productId } = data;
  const gaParams = ['sending_inquiry', 'submit', productId];
  const fbParams = [
    'SendingInquiry',
    {
      product_id: productId
    }
  ];
  pushAnalytics(gaParams, fbParams);
  if (callNativeHandler) {
    sendAnalytics({
      name: 'sending_inquiry',
      props: {
        productId
      }
    });
  }
};

export const onViewingProduct = (data, { callNativeHandler } = {}) => {
  gtag('event', 'viewing_product', data);
  if (callNativeHandler) {
    sendAnalytics({
      name: 'viewing_product',
      props: {
        data
      }
    });
  }
};

export const chatAction1Click = (message) => {
  const gaParams = ['ChatAction', message, getShowcaseId()];
  const fbParams = [
    'ChatAction',
    {
      content_type: message
    },
    {
      showcase_id: getShowcaseId()
    }
  ];
  pushAnalytics(gaParams, fbParams);
  gtag('event', 'ChatAction', {
    event_label: message,
    event_category: 'Click'
  });
};

export const chatConnectClick = () => {
  const gaParams = ['chat_connect', 'click', getShowcaseId()];
  const fbParams = [
    'ChatConnect',
    {
      showcase_id: getShowcaseId()
    }
  ];
  pushAnalytics(gaParams, fbParams);
  gtag('event', 'chat_clicked', {
    from: 'Chat Connect'
  });
};

export const onResellDownloadClick = () => {
  const gaParams = ['resell_download', 'click', getShowcaseId()];
  const fbParams = [
    'ResellDownload',
    {
      showcase_id: getShowcaseId()
    }
  ];
  pushAnalytics(gaParams, fbParams);
};

export const onOrderBarClick = (data, { callNativeHandler } = {}) => {
  const gaParams = ['order_bar', 'click', getShowcaseId()];
  const fbParams = [
    'OrderBarClick',
    {
      showcase_id: getShowcaseId()
    }
  ];
  pushAnalytics(gaParams, fbParams);
  gtag('event', 'order_bar', data);
  if (callNativeHandler) {
    sendAnalytics({
      name: 'order_bar'
    });
  }
};

export const onReviewActionClick = (data, { callNativeHandler } = {}) => {
  const gaParams = ['review_action', 'click', getShowcaseId()];
  const fbParams = [
    'ReviewAction',
    {
      showcase_id: getShowcaseId()
    }
  ];
  pushAnalytics(gaParams, fbParams);
  gtag('event', 'review_action', data);
  if (callNativeHandler) {
    sendAnalytics({
      name: 'review_action'
    });
  }
};

export const onReviewCallClick = () => {
  const gaParams = ['review_call', 'click', getShowcaseId()];
  const fbParams = [
    'ReviewCall',
    {
      showcase_id: getShowcaseId()
    }
  ];
  pushAnalytics(gaParams, fbParams);
};

export const onReviewChatClick = () => {
  const gaParams = ['review_chat', 'click', getShowcaseId()];
  const fbParams = [
    'ReviewChatClicked',
    {
      showcase_id: getShowcaseId()
    }
  ];
  pushAnalytics(gaParams, fbParams);
  gtag('event', 'chat_clicked', {
    from: 'Review'
  });
};

export const onReviewSendOrderClick = () => {
  const gaParams = ['review_send_order', 'click', getShowcaseId()];
  const fbParams = [
    'ReviewSendOrder',
    {
      showcase_id: getShowcaseId()
    }
  ];
  pushAnalytics(gaParams, fbParams);
  gtag('event', 'review_send_order', {
    event_label: 'Review Send Order',
    event_category: 'Click'
  });
};

export const onDownloadCataloguePDF = (data, { callNativeHandler } = {}) => {
  const { companyId } = data;
  const gaParams = ['download_catalogue_pdf_web', 'click', companyId];
  const fbParams = [
    'DownloadCatalogueWebPdf',
    {
      company_id: companyId
    }
  ];
  pushAnalytics(gaParams, fbParams);
  gtag('event', 'download_catalogue_pdf_web', {
    company_id: companyId,
    event_category: 'Click'
  });
  if (callNativeHandler) {
    sendAnalytics({
      name: 'download_catalogue_pdf_web',
      props: {
        companyId
      }
    });
  }
};

export const onGridChatClick = ({ callNativeHandler } = {}) => {
  const gaParams = ['grid_chat', 'click'];
  const fbParams = ['GridChatClicked'];
  pushAnalytics(gaParams, fbParams);
  gtag('event', 'chat_clicked', {
    from: 'Grid'
  });
  if (callNativeHandler) {
    sendAnalytics({
      name: 'grid_chat'
    });
  }
};

export const onCustomPageChatClick = ({ callNativeHandler } = {}) => {
  const gaParams = ['custom_page_chat', 'click'];
  const fbParams = ['CustomePageChatClicked'];
  pushAnalytics(gaParams, fbParams);
  gtag('event', 'chat_clicked', {
    from: 'Custom Page'
  });
  if (callNativeHandler) {
    sendAnalytics({
      name: 'custom_page_chat'
    });
  }
};

export const onCloseBannerClick = () => {
  const gaParams = ['close_banner', 'click'];
  const fbParams = ['CloseBannerClick'];
  pushAnalytics(gaParams, fbParams);
  gtag('event', 'close_banner', {
    event_label: 'Close Banner',
    event_category: 'Click'
  });
};

export const onOpenBannerClick = (url, title) => {
  const gaParams = ['catalogue_banner_clicked', 'click'];
  let fbParams = [];
  if (title) {
    fbParams = [
      'CatalogueBannerClick',
      {
        content_name: title,
        value: url
      }
    ];
    gtag('event', 'banner_clicked', {
      banner_image_url: url,
      banner_name: title
    });
  } else {
    fbParams = [
      'CatalogueBannerClick',
      {
        value: url
      }
    ];
    gtag('event', 'banner_clicked', {
      banner_image_url: url
    });
  }
  pushAnalytics(gaParams, fbParams);
};

export const onHomeChatClick = ({ callNativeHandler } = {}) => {
  gtag('event', 'chat_clicked', {
    from: 'Home'
  });
  const gaParams = ['home_chat', 'click'];
  const fbParams = ['HomeChatClicked'];
  pushAnalytics(gaParams, fbParams);
  if (callNativeHandler) {
    sendAnalytics({
      name: 'home_chat'
    });
  }
};

export const onGalleryChatClick = (data, { callNativeHandler } = {}) => {
  const { productId } = data;
  const gaParams = ['gallery_chat', 'click', productId];
  const fbParams = [
    'GalleryChatClicked',
    {
      product_id: productId
    }
  ];
  pushAnalytics(gaParams, fbParams);
  gtag('event', 'chat_clicked', {
    event_label: productId,
    from: 'Gallery'
  });
  if (callNativeHandler) {
    sendAnalytics({
      name: 'gallery_chat',
      props: {
        productId
      }
    });
  }
};

export const onConfirmOrderClick = (data, { callNativeHandler } = {}) => {
  const gaParams = ['confirm_order', 'click'];
  const fbParams = ['ConfirmOrder'];
  pushAnalytics(gaParams, fbParams);
  gtag('event', 'confirm_order', data);
  if (callNativeHandler) {
    sendAnalytics({
      name: 'confirm_order'
    });
  }
};

export const onConfirmOrderSubmitClick = (data, { callNativeHandler } = {}) => {
  const gaParams = ['confirm_order_submit', 'click'];
  const fbParams = ['ConfirmOrderSubmit'];
  pushAnalytics(gaParams, fbParams);
  gtag('event', 'order_confirmed', data);
  if (callNativeHandler) {
    sendAnalytics({
      name: 'confirm_order_submit'
    });
  }
};

export const onGroupWhatsAppChatClick = () => {
  const gaParams = ['group_contact_whatsapp', 'click', getGroupShowcaseId()];
  const fbParams = [
    'GroupContactWhatsapp',
    {
      showcase_id: getGroupShowcaseId()
    }
  ];
  pushAnalytics(gaParams, fbParams);
  gtag('event', 'group_contact_whatsapp', {
    event_label: 'Group Contact Whatsapp',
    event_category: 'Click'
  });
};

export const onGroupCallClick = () => {
  const gaParams = ['group_contact_call', 'click', getGroupShowcaseId()];
  const fbParams = [
    'GroupContactCall',
    {
      showcase_id: getGroupShowcaseId()
    }
  ];
  pushAnalytics(gaParams, fbParams);
  gtag('event', 'group_contact_call', {
    event_label: 'Group Contact Call',
    event_category: 'Click'
  });
};

export const onGroupQsAdTileClick = () => {
  const gaParams = ['group_footerbanner', 'click', getGroupShowcaseId()];
  const fbParams = [
    'GroupFooterBanner',
    {
      showcase_id: getGroupShowcaseId()
    }
  ];
  pushAnalytics(gaParams, fbParams);
  gtag('event', 'group_footerbanner', {
    event_label: 'Group Footerbanner',
    event_category: 'Click'
  });
};

export const onGroupCatalogueClick = ({ callNativeHandler } = {}) => {
  const groupShowcaseId = getGroupShowcaseId();
  const gaParams = ['group_catalogue', 'click', groupShowcaseId];
  const fbParams = [
    'GroupCatalogueClick',
    {
      showcase_id: groupShowcaseId
    }
  ];
  pushAnalytics(gaParams, fbParams);
  gtag('event', 'group_catalogue', {
    event_label: groupShowcaseId,
    event_category: 'Click'
  });
  if (callNativeHandler) {
    sendAnalytics({
      name: 'group_catalogue',
      props: {
        groupShowcaseId
      }
    });
  }
};

export const onProductTileClick = (source, data, { callNativeHandler } = {}) => {
  gtag('event', 'clicked_product_tile', data);
  const gaParams = ['clicked_product_tile', 'click', source];
  const fbParams = ['ClickedProductTile', source];
  pushAnalytics(gaParams, fbParams);
  if (callNativeHandler) {
    sendAnalytics({
      name: 'clicked_product_tile',
      props: {
        source
      }
    });
  }
};

export const onGroupSearchClick = (searchTerm, { callNativeHandler } = {}) => {
  const gaParams = ['search_performed', 'click', searchTerm];
  const fbParams = [
    'SearchPerformed',
    {
      search_string: searchTerm
    }
  ];
  pushAnalytics(gaParams, fbParams);
  if (callNativeHandler) {
    sendAnalytics({
      name: 'search_performed',
      props: {
        searchTerm
      }
    });
  }
};

export const onInquiryAddedSuccessfully = (data, { callNativeHandler } = {}) => {
  gtag('event', 'added_to_cart_successfully', data);
  if (callNativeHandler) {
    sendAnalytics({
      name: 'added_to_cart_successfully'
    });
  }
};

export const onRequestIdentityModalShown = () => {
  gtag('event', 'request_visitor_identity_shown', {
    event_label: 'Request Visitor Identity Shown',
    event_category: 'Visitor Actions'
  });
};

export const onRequestIdentityModalCompleted = () => {
  gtag('event', 'request_visitor_identity_completed', {
    event_label: 'Request Visitor Identity Completed',
    event_category: 'Visitor Actions'
  });
};

export const onMandatoryLoginShown = () => {
  gtag('event', 'mandatory_login_shown', {
    event_label: 'Mandatory Login Shown',
    event_category: 'Login Actions'
  });
};

export const onMandatoryLoginCompleted = () => {
  gtag('event', 'mandatory_login_completed', {
    event_label: 'Mandatory Login Completed',
    event_category: 'Login Actions'
  });
};

export const initializeCompanyFacebookPixels = (visitor) => {
  if (companyFacebookPixelInitialized) {
    return;
  }
  const companyFacebookPixelIds = getCompanyFacebookPixelIds();
  if (!companyFacebookPixelIds || companyFacebookPixelIds.length === 0) {
    return;
  }
  companyFacebookPixelInitialized = true;
  if (window.fbq) {
    const options = {};
    if (visitor.email) {
      options.em = visitor.email;
    }
    if (visitor.phone) {
      options.ph = visitor.phone.slice(1);
    }
    if (visitor.name) {
      const [firstName, ...lastNameParts] = (visitor.name || '').toLowerCase().split(' ');
      const lastName = lastNameParts.join(' ');
      if (firstName) {
        options.fn = firstName;
      }
      if (lastNameParts.length) {
        options.ln = lastName;
      }
    }
    companyFacebookPixelIds.forEach((companyFacebookPixelId) => {
      if (companyFacebookPixelId) {
        window.fbq('init', companyFacebookPixelId, options);
        window.fbq('trackSingle', companyFacebookPixelId, 'PageView');
      }
    });
  }
};

export const initializeCompanyGoogleAnalytics = (visitor) => {
  if (companyGoogleAnalyticsInitialized) {
    return;
  }
  const companyGoogleAnalyticsIds = getCompanyGoogleAnalyticsIds();
  if (!companyGoogleAnalyticsIds || companyGoogleAnalyticsIds.length === 0) {
    return;
  }
  companyGoogleAnalyticsInitialized = true;
  const options = { groups: 'default', debug_mode: true };
  if (visitor.email) {
    options.email = visitor.email;
  }
  if (visitor.phone) {
    options.phone = visitor.phone.slice(1);
  }
  if (visitor.name) {
    const [firstName, ...lastNameParts] = (visitor.name || '').toLowerCase().split(' ');
    const lastName = lastNameParts.join(' ');
    if (firstName) {
      options.fullname = firstName;
    }
    if (lastNameParts.length) {
      options.lastname = lastName;
    }
  }
  const { pathname } = location;
  options.path_name = pathname;
  gtag('js', new Date());
  companyGoogleAnalyticsIds.forEach((companyGoogleAnalyticsId) => {
    // skip if companyGoogleAnalyticsId is of universal analytics type UA-XXXXXX-X
    // universal analytics is only handled in analytics.js, and not gtag.js script
    // universal analytics is deprecated and will be removed in 2023 as per google
    if (!!companyGoogleAnalyticsId && !companyGoogleAnalyticsId.startsWith('UA-')) {
      gtag('config', companyGoogleAnalyticsId, options);
    }
  });
};

export const onReviewChatNowClick = () => {
  const gaParams = ['order_screen_chat_button_clicked', 'click', getGroupShowcaseId()];
  const fbParams = [
    'OrderScreenChatButton',
    {
      showcase_id: getGroupShowcaseId()
    }
  ];
  pushAnalytics(gaParams, fbParams);
  gtag('event', 'chat_clicked', {
    from: 'Order'
  });
};
